import React, { useContext } from 'react';
import { CloudDownload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthorizations } from 'hooks';
import PropTypes from 'prop-types';
import { Button, Card } from '@totalsoft/rocket-ui';
import PrintComponent from './PrintComponent';
import { Table, Thead, Tbody, Tr, Th } from 'react-super-responsive-table';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import FakeTextWrapper from 'components/common/FakeTextWrapper';
import { PrintsTableContainer } from './PrintStyles';
import { CardActionsContainer } from 'components/common/CardStyles';

const PrintsComponent = ({
  loadingPrints,
  loadingExport,
  prints,
  signingInProgress,
  signingDisabled,
  printDisabled,
  onSignDocuments,
  onResetDocumentSigning,
  onPrintDocument,
  onPrintDocuments,
  onSelectPrint,
  onSignatureTypeChange,
  onDownloadFile,
  onOpenHistoryModal
}) => {
  const { t } = useTranslation();
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [AuthorizationCodes.hasRightToSignDocuments, AuthorizationCodes.hasRightToPrintDocuments],
    documentTypeId,
    documentStateId
  );

  return (
    <Card id='PrintComponent_IconCard' icon={CloudDownload} title={t('Offer.Edit.PrintData.Title')}>
      <PrintsTableContainer container>
        <Table>
          <Thead>
            <Tr>
              <Th>{t('Offer.Edit.PrintData.Selected')}</Th>
              <Th></Th>
              <Th>{t('Offer.Edit.PrintData.DocumentName')}</Th>
              <Th>{t('Offer.Edit.PrintData.Entity')}</Th>
              <Th>{t('Offer.Edit.PrintData.GeneratedBy')}</Th>
              <Th>{t('Offer.Edit.PrintData.Status')}</Th>
              <Th>{t('Offer.Edit.PrintData.SignatureDate')}</Th>
              <Th>{t('Offer.Edit.PrintData.SignatureType')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loadingPrints &&
              prints.map((print, index) => {
                return (
                  <PrintComponent
                    key={index}
                    index={index}
                    print={print}
                    onPrintDocument={onPrintDocument}
                    onSelectPrint={onSelectPrint}
                    onSignatureTypeChange={onSignatureTypeChange}
                    onDownloadFile={onDownloadFile}
                    onOpenHistoryModal={onOpenHistoryModal}
                    onResetDocumentSigning={onResetDocumentSigning}
                    readOnly={globalReadOnly}
                  />
                );
              })}
          </Tbody>
        </Table>
        {loadingPrints && <FakeTextWrapper sx={{ mt: 2 }} />}
        <CardActionsContainer mt={2}>
          <Button
            id='prints_SignBtn'
            color={'primary'}
            onClick={onSignDocuments}
            disabled={
              globalReadOnly({
                controlName: 'Documents.Sign',
                hasAuthorization: authorizations?.hasRightToSignDocuments
              }) ||
              signingInProgress ||
              signingDisabled
            }
          >
            {t('Offer.Edit.PrintData.Sign')}
          </Button>
          <Button
            id='prints_PrintBtn'
            color={'primary'}
            onClick={onPrintDocuments}
            disabled={loadingExport || printDisabled || !authorizations?.hasRightToPrintDocuments}
          >
            {loadingExport ? t('Offer.Edit.PrintData.Printing') : t('Offer.Edit.PrintData.Print')}
          </Button>
        </CardActionsContainer>
      </PrintsTableContainer>
    </Card>
  );
};

PrintsComponent.propTypes = {
  loadingPrints: PropTypes.bool.isRequired,
  loadingExport: PropTypes.bool.isRequired,
  prints: PropTypes.array.isRequired,
  signingInProgress: PropTypes.bool.isRequired,
  signingDisabled: PropTypes.bool.isRequired,
  printDisabled: PropTypes.bool.isRequired,
  onSignDocuments: PropTypes.func.isRequired,
  onResetDocumentSigning: PropTypes.func.isRequired,
  onPrintDocuments: PropTypes.func.isRequired,
  onPrintDocument: PropTypes.func.isRequired,
  onSelectPrint: PropTypes.func.isRequired,
  onSignatureTypeChange: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onOpenHistoryModal: PropTypes.func.isRequired
};

export default PrintsComponent;
